import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaComments, FaUser, FaPaperPlane, FaUserPlus, FaTimes, FaHeart } from 'react-icons/fa';

const priorityOptions = [
  { value: 'normal', label: 'Normal', icon: '💬', color: 'text-gray-600' },
  { value: 'announcement', label: 'Announcement', icon: '📢', color: 'text-orange-600', desc: 'Notifies everyone' },
  { value: 'emergency', label: 'Emergency', icon: '🚨', color: 'text-red-600', desc: 'Urgent notification' }
];

export function ChatTab({ messages, messageInput, setMessageInput, onSendMessage, currentUser, familyMembers, familyId }) {
  const chatEndRef = useRef(null);
  const [messagePriority, setMessagePriority] = useState('normal');
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [showInvitePrompt, setShowInvitePrompt] = useState(false);
  const [invitePromptDismissed, setInvitePromptDismissed] = useState(false);

  // Show invite prompt after 5+ messages and family has < 5 members
  useEffect(() => {
    const promptShownKey = `invite_prompt_shown_${familyId}`;
    const alreadyShown = sessionStorage.getItem(promptShownKey);
    
    if (!alreadyShown && !invitePromptDismissed && messages.length >= 5 && (familyMembers?.length || 0) < 5) {
      const timer = setTimeout(() => {
        setShowInvitePrompt(true);
        sessionStorage.setItem(promptShownKey, 'true');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [messages.length, familyMembers?.length, familyId, invitePromptDismissed]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const dismissInvitePrompt = () => {
    setShowInvitePrompt(false);
    setInvitePromptDismissed(true);
  };

  // Parse @mentions from message input
  const parseMentions = (text) => {
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      const member = familyMembers?.find(
        m => m.name.toLowerCase().replace(/\s+/g, '').includes(match[1].toLowerCase())
      );
      if (member) {
        mentions.push(member.user_id);
      }
    }
    return mentions;
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessageInput(value);
    
    const lastAt = value.lastIndexOf('@');
    if (lastAt !== -1) {
      const afterAt = value.substring(lastAt + 1);
      if (!afterAt.includes(' ')) {
        setMentionFilter(afterAt.toLowerCase());
        setShowMentionSuggestions(true);
      } else {
        setShowMentionSuggestions(false);
      }
    } else {
      setShowMentionSuggestions(false);
    }
  };

  const insertMention = (member) => {
    const lastAt = messageInput.lastIndexOf('@');
    const beforeAt = messageInput.substring(0, lastAt);
    const name = member.name.replace(/\s+/g, '');
    setMessageInput(beforeAt + '@' + name + ' ');
    setShowMentionSuggestions(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    
    const mentions = parseMentions(messageInput);
    onSendMessage(e, messagePriority, mentions);
    setMessagePriority('normal');
  };

  const filteredMembers = familyMembers?.filter(
    m => m.user_id !== currentUser?.user_id && 
         m.name.toLowerCase().includes(mentionFilter)
  ) || [];

  const getPriorityStyle = (priority) => {
    switch(priority) {
      case 'announcement': return 'bg-orange-100 border-l-4 border-orange-500';
      case 'emergency': return 'bg-red-100 border-l-4 border-red-500';
      default: return '';
    }
  };

  return (
    <div className="space-y-4" data-testid="chat-tab-wrap">
      {/* Banner */}
      <div className="rounded-xl overflow-hidden shadow-sm ring-1 ring-black/5" data-testid="chat-banner">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/6e8c71c9-5deb-44de-959d-34c14877fd56/images/2cb7306cfe03361fb28ad7774d732091619d5020069e4316eeecd3e7fade9821.jpeg"
          alt="A multi-generational family gathered together in the kitchen, chatting and smiling at the camera"
          className="w-full h-auto"
          loading="lazy"
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 flex flex-col" style={{ height: '600px' }} data-testid="chat-tab">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <FaComments className="text-6xl mx-auto mb-4 text-gray-300" />
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isCurrentUser = message.user_id === currentUser?.user_id;
            const priority = message.priority || 'normal';
            return (
              <div
                key={message.message_id}
                className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${message.message_id}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    isCurrentUser
                      ? priority === 'emergency' ? 'bg-red-600 text-white' :
                        priority === 'announcement' ? 'bg-orange-600 text-white' :
                        'bg-blue-600 text-white'
                      : `bg-gray-200 text-gray-800 ${getPriorityStyle(priority)}`
                  }`}
                >
                  {!isCurrentUser && (
                    <p className="text-xs font-semibold mb-1">{message.user_name}</p>
                  )}
                  {priority !== 'normal' && (
                    <p className="text-xs font-bold mb-1">
                      {priority === 'announcement' ? '📢 ANNOUNCEMENT' : '🚨 EMERGENCY'}
                    </p>
                  )}
                  <p>{message.content}</p>
                  <p className={`text-xs mt-1 ${isCurrentUser ? 'text-blue-100' : 'text-gray-600'}`}>
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Message Input */}
      <form
        onSubmit={handleSubmit}
        className="border-t p-4"
        data-testid="chat-input-form"
      >
        {/* Invite Family Prompt - shown contextually */}
        {showInvitePrompt && (
          <div className="mb-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-3 animate-fadeIn">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaHeart className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-emerald-900 font-medium text-sm">Enjoying the conversation?</p>
                  <p className="text-emerald-700 text-xs mt-0.5">Invite more family members to join the fun!</p>
                  <Link 
                    to={`/family/${familyId}/members`}
                    className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-full hover:bg-emerald-700 transition"
                    data-testid="invite-family-btn"
                  >
                    <FaUserPlus className="text-xs" />
                    Invite Family
                  </Link>
                </div>
              </div>
              <button
                type="button"
                onClick={dismissInvitePrompt}
                className="text-emerald-400 hover:text-emerald-600 p-1"
                title="Dismiss"
                data-testid="dismiss-invite-prompt"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* Mention Suggestions */}
        {showMentionSuggestions && filteredMembers.length > 0 && (
          <div className="mb-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
            {filteredMembers.map(m => (
              <button
                key={m.user_id}
                type="button"
                onClick={() => insertMention(m)}
                className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center space-x-2"
              >
                <FaUser className="text-gray-400" />
                <span className="text-gray-800">@{m.name}</span>
              </button>
            ))}
          </div>
        )}
        
        {/* Priority Selector */}
        <div className="mb-2 flex items-center space-x-2">
          <span className="text-sm text-gray-500">Priority:</span>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPriorityMenu(!showPriorityMenu)}
              className={`px-3 py-1 rounded-full text-sm flex items-center space-x-1 border ${
                messagePriority === 'announcement' ? 'bg-orange-100 border-orange-300 text-orange-700' :
                messagePriority === 'emergency' ? 'bg-red-100 border-red-300 text-red-700' :
                'bg-gray-100 border-gray-300 text-gray-700'
              }`}
              data-testid="priority-selector"
            >
              <span>{priorityOptions.find(p => p.value === messagePriority)?.icon}</span>
              <span>{priorityOptions.find(p => p.value === messagePriority)?.label}</span>
            </button>
            
            {showPriorityMenu && (
              <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48 z-10">
                {priorityOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setMessagePriority(option.value);
                      setShowPriorityMenu(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${option.color}`}
                    data-testid={`priority-${option.value}`}
                  >
                    <span className="mr-2">{option.icon}</span>
                    <span>{option.label}</span>
                    {option.desc && <p className="text-xs text-gray-500 mt-0.5">{option.desc}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex space-x-2">
          <input
            type="text"
            value={messageInput}
            onChange={handleInputChange}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            placeholder="Type a message... (use @ to mention)"
            data-testid="message-input"
          />
          <button
            type="submit"
            className={`px-6 py-2 text-white rounded-lg ${
              messagePriority === 'emergency' ? 'bg-red-600 hover:bg-red-700' :
              messagePriority === 'announcement' ? 'bg-orange-600 hover:bg-orange-700' :
              'bg-blue-600 hover:bg-blue-700'
            }`}
            data-testid="send-message-btn"
          >
            <FaPaperPlane />
          </button>
        </div>
      </form>
    </div>
    </div>
  );
}
